/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo, useRef } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import {
	LazyRovoOnboardingGifDarkIcon,
	LazyRovoOnboardingGifDarkIconConfluence,
	LazyRovoOnboardingGifLightIcon,
	LazyRovoOnboardingGifLightIconConfluence,
} from '../../onboarding-gifs';

import messages from './messages';

const styles = cssMap({
	container: {
		width: '100%',
		height: '100%',
		display: 'flex',
		flexGrow: 1,
		flexDirection: 'column',
		paddingBottom: token('space.150'),
		justifyContent: 'space-between',
	},
	wrapper: {
		width: '100%',
		height: '100%',
		flexGrow: 1,
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingLeft: token('space.300'),
	},
	videoContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		backgroundClip: 'padding-box',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
	},
	buttonContainer: {
		width: '100%',
		height: '64px',
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'flex-end',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

type ProductType = 'jira' | 'confluence';

export const ChatOnboardingGreetings = ({
	onNext,
	onStepViewed,
	product,
}: {
	onNext: (buttonName: string, additionalAttributes: Record<string, string>) => void;
	onStepViewed: (additionalAttributes: Record<string, string>) => void;
	product: ProductType;
}): JSX.Element => {
	const { colorMode } = useThemeObserver();
	const hasClickedRef = useRef(false);

	useEffect(() => {
		onStepViewed({ colorMode: colorMode as string });
	}, [onStepViewed, colorMode]);

	const handleOnClick = () => {
		if (!hasClickedRef.current) {
			hasClickedRef.current = true;
			onNext('continue', { colorMode: colorMode as string });
		}
	};

	const getRovoOnboardingGif = useMemo(() => {
		if (product === 'confluence') {
			return colorMode === 'light' ? (
				<LazyRovoOnboardingGifLightIconConfluence alt="Onboarding Video" width={352} height={196} />
			) : (
				<LazyRovoOnboardingGifDarkIconConfluence alt="Onboarding Video" width={352} height={196} />
			);
		} else if (product === 'jira') {
			return colorMode === 'light' ? (
				<LazyRovoOnboardingGifLightIcon alt="Onboarding Video" width={352} height={196} />
			) : (
				<LazyRovoOnboardingGifDarkIcon alt="Onboarding Video" width={352} height={196} />
			);
		}
	}, [colorMode, product]);

	return (
		<Stack xcss={styles.container}>
			<Stack xcss={styles.wrapper} space="space.200">
				<Box xcss={styles.videoContainer}>{getRovoOnboardingGif}</Box>
				<Heading size="medium">
					<FormattedMessage {...messages.headingForGreetings} />
				</Heading>
				<Text size="medium" as="p">
					<FormattedMessage
						{...(product === 'confluence'
							? messages.descriptionForGreetingsConnie
							: messages.descriptionForGreetings)}
					/>
				</Text>
			</Stack>
			<Box xcss={styles.buttonContainer}>
				<Button appearance="primary" onClick={handleOnClick}>
					<FormattedMessage {...messages.button} />
				</Button>
			</Box>
		</Stack>
	);
};
